import type { MessageChannel } from "@redotech/effect-api-shared/models/shopper-ai/conversation/message";
import type { ConversationThread } from "@redotech/effect-api-shared/models/shopper-ai/conversation/thread";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { and, eq, useLiveQuery } from "@tanstack/react-db";
import { Fragment, memo, useCallback, useState } from "react";
import { useChatThreadView } from "../../hooks/use-chat-thread-view";
import { useThreadMessages } from "../../hooks/use-conversation-data";
import { useGlobalConversationCollections } from "../../providers/global-conversation-collections-context";
import { MessageInput } from "../message-input";
import { ChatMessageBubble } from "../message-types/chat-message-bubble";
import { SendingChatBubble } from "../message-types/sending-chat-bubble";
import { ThreadHeader } from "../thread-header";

interface ChatThreadViewProps {
  thread: ConversationThread;
  channel: MessageChannel;
}

export const ChatThreadView = memo(function ChatThreadView({
  thread,
  channel,
}: ChatThreadViewProps) {
  const { data: messages } = useThreadMessages(
    thread.conversationId,
    thread.id,
  );
  const { messageGroups, lastMessageId } = useChatThreadView(messages);

  const collections = useGlobalConversationCollections();
  const draftsResult = useLiveQuery(
    (q) =>
      q
        .from({ draft: collections.drafts })
        .where(({ draft }) =>
          and(
            eq(draft.conversationId, String(thread.conversationId)),
            eq(draft.threadId, String(thread.id)),
          ),
        ),
    [thread.conversationId, thread.id],
  );
  const sendingDraft = (draftsResult.data ?? []).find(
    (d) => d.status === "SENDING",
  );

  const [isCollapsed, setIsCollapsed] = useState(false);

  const toggleCollapsed = useCallback(() => {
    setIsCollapsed((prev) => !prev);
  }, []);

  return (
    <Flex dir="column" gap="none">
      <ThreadHeader
        channel={channel}
        isCollapsed={isCollapsed}
        latestMessageTime={messages.at(-1)?.sentAt}
        onClick={toggleCollapsed}
        subject={null}
      />
      {!isCollapsed && (
        <Flex
          bgColor="primary"
          borderBottomWidth="1px"
          borderColor="primary"
          borderLeftWidth="1px"
          borderRightWidth="1px"
          borderStyle="solid"
          bottomLeftRadius="sm"
          bottomRightRadius="sm"
          dir="column"
          gap="none"
        >
          <Flex dir="column" gap="lg" overflowY="auto" p="xl">
            {messageGroups.length === 0 ? (
              <Flex align="center" grow={1} justify="center" p="xl">
                <Text fontSize="md" textColor="secondary">
                  No messages in this thread
                </Text>
              </Flex>
            ) : (
              messageGroups.map((group) => (
                <Fragment key={group.dateLabel}>
                  <DateDivider label={group.dateLabel} />
                  {group.messages.map((formattedMessage) => (
                    <ChatMessageBubble
                      key={formattedMessage.id}
                      message={formattedMessage}
                    />
                  ))}
                </Fragment>
              ))
            )}
            {sendingDraft && <SendingChatBubble draft={sendingDraft} />}
          </Flex>
          <Flex
            borderColor="secondary"
            borderStyle="solid"
            borderTopWidth="1px"
            p="xl"
          >
            <MessageInput
              channel={channel}
              conversationId={thread.conversationId}
              inReplyToMessageId={lastMessageId ?? undefined}
              mode="chat"
              threadId={thread.id}
            />
          </Flex>
        </Flex>
      )}
    </Flex>
  );
});

const DateDivider = memo(function DateDivider({ label }: { label: string }) {
  return (
    <Flex align="center" gap="md" w="full">
      <Flex
        borderBottomWidth="1px"
        borderColor="secondary"
        borderStyle="solid"
        grow={1}
      />
      <Text fontSize="xs" textColor="tertiary" whiteSpace="nowrap">
        {label}
      </Text>
      <Flex
        borderBottomWidth="1px"
        borderColor="secondary"
        borderStyle="solid"
        grow={1}
      />
    </Flex>
  );
});
